import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Image,
  InlineStack,
  Modal,
  Page,
  Select,
  SkeletonBodyText,
  SkeletonDisplayText,
  Text,
  Tooltip,
} from "@shopify/polaris";
import {
  fetchWithSessionToken,
  redirectToAuth,
  redirectToExternal,
} from "../lib/appBridge";
import { PricingCard } from "../components/PricingCard";
import { Knob } from "../components/Knob";
import { trackAnalyticsEvent } from "../lib/analytics";

const DEFAULT_GENSEO_URL = "https://app.genseo.co";
const CONNECT_STATUS_POLL_INTERVAL_MS = 3_000;
const CONNECT_STATUS_POLL_TIMEOUT_MS = 120_000;
const BILLING_RETURN_FLAG_KEY = "genseo_shopify_billing_return";
const BILLING_RETURN_WINDOW_MS = 30 * 60 * 1000;
const ENTITLEMENT_RETRY_DELAYS_MS = [0, 800, 1800];
const getGenseoBaseUrl = () =>
  (import.meta.env.VITE_GENSEO_APP_URL as string | undefined) || DEFAULT_GENSEO_URL;

const normalizeShopDomain = (rawShop: string) => {
  const cleaned = rawShop.trim().toLowerCase().replace(/^https?:\/\//, "");
  const withoutQuery = cleaned.split("?")[0];
  const adminStoreMatch = withoutQuery.match(/admin\.shopify\.com\/store\/([^/]+)/);
  if (adminStoreMatch?.[1]) {
    return `${adminStoreMatch[1]}.myshopify.com`;
  }
  const hostOnly = withoutQuery.split("/")[0];
  if (hostOnly.includes(".myshopify.com")) {
    return hostOnly;
  }
  if (!hostOnly.includes(".")) {
    return `${hostOnly}.myshopify.com`;
  }
  return hostOnly;
};

type UiStep = "SUBSCRIPTION" | "CONNECT";
type PlanTier = "STARTER" | "PRO" | "EXISTING_GENSEO";
const ACTIVE_SUBSCRIPTION_STATUSES = new Set([
  "active",
  "accepted",
  "trialing",
  "pending",
  "pending_active",
]);

const isSubscriptionActive = (hasSubscription: boolean, status?: string | null) => {
  const normalizedStatus = (status || "").toLowerCase();
  if (ACTIVE_SUBSCRIPTION_STATUSES.has(normalizedStatus)) {
    return true;
  }
  if (normalizedStatus.length > 0) {
    return false;
  }
  return hasSubscription;
};

const normalizeSubscriptionStatusLabel = (status?: string | null) => {
  const normalized = (status || "").toLowerCase();
  if (normalized === "cancelled" || normalized === "canceled") return "inactive";
  return normalized;
};

const mergeSubscriptionState = (nativeStatus: {
  hasSubscription: boolean;
  subscriptionStatus: string | null;
  subscriptionName: string | null;
}, billingStatus: {
  hasSubscription: boolean;
  subscriptionStatus: string | null;
  subscriptionName: string | null;
}) => {
  const nativeActive = isSubscriptionActive(
    nativeStatus.hasSubscription,
    nativeStatus.subscriptionStatus
  );
  const billingActive = isSubscriptionActive(
    billingStatus.hasSubscription,
    billingStatus.subscriptionStatus
  );

  const hasSubscription = nativeStatus.hasSubscription || billingStatus.hasSubscription;
  const hasActiveSubscription = nativeActive || billingActive;

  // Prefer real-time billing status/name when it is active.
  const subscriptionStatus = billingActive
    ? billingStatus.subscriptionStatus
    : nativeActive
      ? nativeStatus.subscriptionStatus
      : billingStatus.subscriptionStatus || nativeStatus.subscriptionStatus;

  const subscriptionName = billingActive
    ? billingStatus.subscriptionName
    : nativeActive
      ? nativeStatus.subscriptionName
      : billingStatus.subscriptionName || nativeStatus.subscriptionName;

  return {
    hasSubscription,
    hasActiveSubscription,
    subscriptionStatus,
    subscriptionName,
  };
};

const wait = (ms: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const getPendingProductSyncStorageKey = (shop: string) =>
  `genseo:pending-initial-product-sync:${shop}`;

const getProductSyncPreferenceStorageKey = (shop: string) =>
  `genseo:product-sync-preference:${shop}`;

const getSkipShopifyPaymentStorageKey = (shop: string) =>
  `genseo:skip-shopify-payment:${shop}`;

function ConnectFlowPage() {
  const [searchParams] = useSearchParams();
  const [error] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [hasMapping, setHasMapping] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [blogsLoading, setBlogsLoading] = useState(false);
  const [blogsError, setBlogsError] = useState<string | null>(null);
  const [blogs, setBlogs] = useState<Array<{ id: string; title: string }>>([]);
  const [selectedBlogId, setSelectedBlogId] = useState<string>("");
  const [mappingSaving, setMappingSaving] = useState(false);
  const [isConnectionPolling, setIsConnectionPolling] = useState(false);
  const [forceReconnectMode, setForceReconnectMode] = useState(false);
  const [uiStep, setUiStep] = useState<UiStep>("SUBSCRIPTION");
  const [isOpeningPricing, setIsOpeningPricing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanTier>("PRO");
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [subscriptionName, setSubscriptionName] = useState<string | null>(null);
  const [productSyncEnabled, setProductSyncEnabled] = useState(true);
  const [productSyncStatus, setProductSyncStatus] = useState("idle");
  const [, setLastProductSyncAt] = useState<string | null>(null);
  const [, setProductSyncError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<number | null>(null);
  const pollingTimeoutRef = useRef<number | null>(null);
  const pollingRequestInFlightRef = useRef(false);
  const initialProductSyncInFlightRef = useRef(false);
  const initialProductSyncAttemptedForShopRef = useRef<string | null>(null);
  const pendingInitialProductSyncRef = useRef(false);
  const isMountedRef = useRef(true);

  const DEFAULT_FIELD_MAP: Record<string, string> = {
    title: "title",
    slug: "handle",
    thumbnail: "image",
    content: "content",
    meta_description: "excerpt",
    excerpt: "excerpt",
    keyword: "tags",
  };
  const [fieldMap, setFieldMap] = useState<Record<string, string>>(
    DEFAULT_FIELD_MAP
  );

  const fieldLabels: Record<string, string> = {
    title: "Title",
    slug: "Slug",
    thumbnail: "Thumbnail",
    content: "Content",
    meta_description: "Meta description",
    excerpt: "Excerpt",
    keyword: "Keyword",
  };
  const shopifyFieldOptions = [
    { label: "Title", value: "title" },
    { label: "Handle", value: "handle" },
    { label: "Image", value: "image" },
    { label: "Content", value: "content" },
    { label: "Excerpt", value: "excerpt" },
    { label: "Tags", value: "tags" },
  ];

  const shop = searchParams.get("shop") || "";
  const host = searchParams.get("host") || "";
  const chargeId = searchParams.get("charge_id") || "";
  const isPaymentTestMode = searchParams.get("genseoTestPayment") === "1";

  const markManagedPricingReturn = useCallback(() => {
    try {
      window.localStorage.setItem(BILLING_RETURN_FLAG_KEY, String(Date.now()));
    } catch {
      // ignore storage issues
    }
  }, []);

  const consumeManagedPricingReturn = useCallback(() => {
    try {
      const raw = window.localStorage.getItem(BILLING_RETURN_FLAG_KEY);
      if (!raw) return false;
      window.localStorage.removeItem(BILLING_RETURN_FLAG_KEY);
      const timestamp = Number(raw);
      if (!Number.isFinite(timestamp)) return false;
      return Date.now() - timestamp <= BILLING_RETURN_WINDOW_MS;
    } catch {
      return false;
    }
  }, []);

  const normalizedShop = useMemo(() => {
    if (!shop) return "";
    return normalizeShopDomain(shop);
  }, [shop]);

  useEffect(() => {
    if (!normalizedShop || uiStep !== "SUBSCRIPTION") return;
    trackAnalyticsEvent("billing_pricing_viewed", {
      route: "/",
      shopDomain: normalizedShop,
      uiStep,
    });
  }, [normalizedShop, uiStep]);

  const targetUrl = useMemo(() => {
    if (!normalizedShop) return "";
    const base = getGenseoBaseUrl().replace(/\/$/, "");
    const params = new URLSearchParams({
      source: "shopify",
      shop: normalizedShop,
      shopDomain: normalizedShop,
      productSyncEnabled: productSyncEnabled ? "true" : "false",
    });
    if (host) {
      params.set("host", host);
    }
    return `${base}/shopify/connect?${params.toString()}`;
  }, [normalizedShop, host, productSyncEnabled]);

  const connectedUrl = useMemo(() => {
    if (!normalizedShop) return "";
    const base = getGenseoBaseUrl().replace(/\/$/, "");
    const params = new URLSearchParams({
      shop: normalizedShop,
      host,
      source: "shopify",
    });
    return `${base}/dashboard?${params.toString()}`;
  }, [normalizedShop, host]);

  const clearConnectionPollingTimers = useCallback(() => {
    if (pollingIntervalRef.current !== null) {
      window.clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingTimeoutRef.current !== null) {
      window.clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    pollingRequestInFlightRef.current = false;
  }, []);

  const stopConnectionPolling = useCallback(() => {
    clearConnectionPollingTimers();
    if (isMountedRef.current) {
      setIsConnectionPolling(false);
    }
  }, [clearConnectionPollingTimers]);

  const attemptReauth = useCallback(
    (reauthorizeUrl?: string) => {
      if (!reauthorizeUrl || !host) return false;
      const redirected = redirectToAuth(host, reauthorizeUrl);
      if (!redirected) {
        if (window.top) {
          window.top.location.href = reauthorizeUrl;
        } else {
          window.location.href = reauthorizeUrl;
        }
      }
      return true;
    },
    [host]
  );

  const fetchNativeConnectionStatus = useCallback(
    async (options: { silent?: boolean } = {}) => {
      if (!host || !normalizedShop) {
        return {
          connected: false,
          hasMapping: false,
          reauthorized: false,
          hasSubscription: false,
          subscriptionStatus: null,
          subscriptionName: null,
        };
      }
      try {
        const res = await fetchWithSessionToken(
          host,
          `/api/native-status?shop=${encodeURIComponent(normalizedShop)}&host=${encodeURIComponent(host)}`
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const message = data?.error || `native-status failed: ${res.status}`;
          const reauthorized = attemptReauth(data?.reauthorizeUrl);
          if (reauthorized) {
            return {
              connected: false,
              hasMapping: false,
              reauthorized: true,
              hasSubscription: false,
              subscriptionStatus: null,
              subscriptionName: null,
            };
          }
          throw new Error(message);
        }

        const connected = Boolean(data?.connected);
        const mapping = Boolean(data?.hasMapping);
        const hasSubscription = Boolean(data?.hasSubscription);
        const nextSubscriptionStatus = data?.subscriptionStatus ?? null;
        const nextSubscriptionName = data?.subscriptionName ?? null;
        const nextProductSyncStatus = data?.productSyncStatus || "idle";
        const nextLastProductSyncAt = data?.lastProductSyncAt || null;
        const nextProductSyncError = data?.productSyncError || null;
        if (isMountedRef.current) {
          setIsConnected(connected);
          setHasMapping(mapping);
          setSubscriptionStatus(nextSubscriptionStatus);
          setSubscriptionName(nextSubscriptionName);
          setHasActiveSubscription(
            isSubscriptionActive(hasSubscription, nextSubscriptionStatus)
          );
          if (connected && mapping) {
            setProductSyncEnabled(Boolean(data?.productSyncEnabled));
          }
          setProductSyncStatus(nextProductSyncStatus);
          setLastProductSyncAt(nextLastProductSyncAt);
          setProductSyncError(nextProductSyncError);
        }
        return {
          connected,
          hasMapping: mapping,
          reauthorized: false,
          hasSubscription,
          subscriptionStatus: nextSubscriptionStatus,
          subscriptionName: nextSubscriptionName,
        };
      } catch (err: any) {
        if (!options.silent && isMountedRef.current) {
          setStatusError(err?.message || "Failed to check connection status");
        }
        return {
          connected: false,
          hasMapping: false,
          reauthorized: false,
          hasSubscription: false,
          subscriptionStatus: null,
          subscriptionName: null,
        };
      }
    },
    [attemptReauth, host, normalizedShop]
  );

  const fetchSubscriptionStatus = useCallback(
    async (options: { silent?: boolean } = {}) => {
      if (!host || !normalizedShop) {
        return {
          ok: false,
          reauthorized: false,
          hasSubscription: false,
          subscriptionStatus: null,
          subscriptionName: null,
        };
      }
      try {
        const params = new URLSearchParams({ shop: normalizedShop, host });
        const res = await fetchWithSessionToken(
          host,
          `/api/subscription-status?${params.toString()}`
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const reauthorized = attemptReauth(data?.reauthorizeUrl);
          if (reauthorized) {
            return {
              ok: false,
              reauthorized: true,
              hasSubscription: false,
              subscriptionStatus: null,
              subscriptionName: null,
            };
          }
          throw new Error(data?.error || "Failed to check subscription status");
        }

        const hasSubscription = Boolean(data?.hasSubscription);
        const nextSubscriptionStatus = data?.subscriptionStatus ?? null;
        const nextSubscriptionName = data?.subscriptionName ?? null;
        if (isMountedRef.current) {
          setSubscriptionStatus(nextSubscriptionStatus);
          setSubscriptionName(nextSubscriptionName);
          setHasActiveSubscription(
            isSubscriptionActive(hasSubscription, nextSubscriptionStatus)
          );
        }

        return {
          ok: true,
          reauthorized: false,
          hasSubscription,
          subscriptionStatus: nextSubscriptionStatus,
          subscriptionName: nextSubscriptionName,
        };
      } catch (err: any) {
        if (!options.silent && isMountedRef.current) {
          setStatusError(err?.message || "Failed to check subscription status");
        }
        return {
          ok: false,
          reauthorized: false,
          hasSubscription: false,
          subscriptionStatus: null,
          subscriptionName: null,
        };
      }
    },
    [attemptReauth, host, normalizedShop]
  );

  const resolveEntitlementGate = useCallback(
    async (options: { retryForReturn?: boolean } = {}) => {
      const retryDelays = options.retryForReturn ? ENTITLEMENT_RETRY_DELAYS_MS : [0];
      let lastResult: {
        nativeConnected: boolean;
        hasSubscription: boolean;
        hasActiveSubscription: boolean;
        subscriptionStatus: string | null;
        subscriptionName: string | null;
      } = {
        nativeConnected: false,
        hasSubscription: false,
        hasActiveSubscription: false,
        subscriptionStatus: null,
        subscriptionName: null,
      };

      for (let attempt = 0; attempt < retryDelays.length; attempt += 1) {
        const delay = retryDelays[attempt];
        if (delay > 0) {
          await wait(delay);
        }

        const [nativeStatus, billingStatus] = await Promise.all([
          fetchNativeConnectionStatus({ silent: true }),
          fetchSubscriptionStatus({ silent: true }),
        ]);

        if (nativeStatus.reauthorized || billingStatus.reauthorized) {
          return { reauthorized: true as const, ...lastResult };
        }

        const mergedSubscription = mergeSubscriptionState(
          {
            hasSubscription: nativeStatus.hasSubscription,
            subscriptionStatus: nativeStatus.subscriptionStatus,
            subscriptionName: nativeStatus.subscriptionName,
          },
          {
            hasSubscription: billingStatus.hasSubscription,
            subscriptionStatus: billingStatus.subscriptionStatus,
            subscriptionName: billingStatus.subscriptionName,
          }
        );

        lastResult = {
          nativeConnected: Boolean(nativeStatus.connected),
          hasSubscription: mergedSubscription.hasSubscription,
          hasActiveSubscription: mergedSubscription.hasActiveSubscription,
          subscriptionStatus: mergedSubscription.subscriptionStatus,
          subscriptionName: mergedSubscription.subscriptionName,
        };

        if (lastResult.hasActiveSubscription) {
          break;
        }
      }

      return { reauthorized: false as const, ...lastResult };
    },
    [fetchNativeConnectionStatus, fetchSubscriptionStatus]
  );

  const startConnectionPolling = useCallback(async () => {
    if (!host || !normalizedShop) return;

    stopConnectionPolling();
    if (isMountedRef.current) {
      setStatusError(null);
      setIsConnectionPolling(true);
    }

    const runConnectionCheck = async () => {
      if (pollingRequestInFlightRef.current) return true;
      pollingRequestInFlightRef.current = true;
      try {
        const result = await fetchNativeConnectionStatus({ silent: true });
        if (result.reauthorized) {
          stopConnectionPolling();
          return false;
        }
        if (result.connected) {
          stopConnectionPolling();
          return false;
        }
        return true;
      } finally {
        pollingRequestInFlightRef.current = false;
      }
    };

    const shouldContinuePolling = await runConnectionCheck();
    if (!isMountedRef.current || !shouldContinuePolling) return;

    pollingIntervalRef.current = window.setInterval(() => {
      void runConnectionCheck();
    }, CONNECT_STATUS_POLL_INTERVAL_MS);

    pollingTimeoutRef.current = window.setTimeout(() => {
      stopConnectionPolling();
      if (isMountedRef.current) {
        setStatusError("Connection has not been confirmed yet. Please try Connect again.");
      }
    }, CONNECT_STATUS_POLL_TIMEOUT_MS);
  }, [
    fetchNativeConnectionStatus,
    host,
    normalizedShop,
    stopConnectionPolling,
  ]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearConnectionPollingTimers();
    };
  }, [clearConnectionPollingTimers]);

  useEffect(() => {
    if (!normalizedShop) {
      pendingInitialProductSyncRef.current = false;
      return;
    }

    try {
      pendingInitialProductSyncRef.current =
        window.localStorage.getItem(getPendingProductSyncStorageKey(normalizedShop)) === "true";
    } catch {
      pendingInitialProductSyncRef.current = false;
    }
  }, [normalizedShop]);

  useEffect(() => {
    let active = true;
    let authRedirected = false;
    if (!normalizedShop || !host) {
      setStatusLoading(false);
      return () => {
        active = false;
      };
    }

    (async () => {
      try {
        setStatusLoading(true);
        setStatusError(null);

        const sessionParams = new URLSearchParams({ shop: normalizedShop });
        if (host) sessionParams.set("host", host);
        const sessionRes = await fetchWithSessionToken(
          host,
          `/api/ensure-session?${sessionParams.toString()}`
        );
        const sessionData = await sessionRes.json().catch(() => ({}));
        if (!sessionRes.ok) {
          if (attemptReauth(sessionData?.reauthorizeUrl)) {
            authRedirected = true;
            return;
          }
          throw new Error(sessionData?.error || "Failed to ensure Shopify session");
        }

        if (!active) return;

        const returnedFromManagedPricing = consumeManagedPricingReturn();
        const returnedFromChargeApproval = Boolean(chargeId);
        const gateStatus = await resolveEntitlementGate({
          retryForReturn: returnedFromManagedPricing || returnedFromChargeApproval,
        });

        if (gateStatus.reauthorized) {
          authRedirected = true;
          return;
        }

	        if (isMountedRef.current) {
	          let skipShopifyPayment = false;
	          try {
	            skipShopifyPayment =
	              window.localStorage.getItem(getSkipShopifyPaymentStorageKey(normalizedShop)) === "true";
	          } catch {}

	          setSubscriptionStatus(gateStatus.subscriptionStatus);
	          setSubscriptionName(gateStatus.subscriptionName);
	          setHasActiveSubscription(gateStatus.hasActiveSubscription);
          setUiStep(
            isPaymentTestMode
              ? "SUBSCRIPTION"
              : returnedFromManagedPricing ||
                  returnedFromChargeApproval ||
                  gateStatus.hasActiveSubscription ||
                  gateStatus.nativeConnected ||
                  skipShopifyPayment
                ? "CONNECT"
                : "SUBSCRIPTION"
          );
	        }
      } catch (err: any) {
        if (!active || authRedirected) return;
        setStatusError(err?.message || "Failed to check connection status");
        setIsConnected(false);
        setHasMapping(false);
        setHasActiveSubscription(false);
        setUiStep("SUBSCRIPTION");
      } finally {
        if (active && !authRedirected) setStatusLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [
    attemptReauth,
    consumeManagedPricingReturn,
    chargeId,
    resolveEntitlementGate,
    host,
    isPaymentTestMode,
    normalizedShop,
  ]);

  useEffect(() => {
    let active = true;
    if (
      statusLoading ||
      ((isConnected && hasMapping) && !forceReconnectMode) ||
      !normalizedShop ||
      !host
    ) {
      return () => {
        active = false;
      };
    }

    (async () => {
      try {
        setBlogsLoading(true);
        setBlogsError(null);
        const params = new URLSearchParams({ shop: normalizedShop });
        if (host) params.set("host", host);
        const res = await fetchWithSessionToken(
          host,
          `/api/blog-posts?${params.toString()}`
        );
        const data = await res.json().catch(() => ({}));
        if (!active) return;
        if (!res.ok) {
          if (attemptReauth(data?.reauthorizeUrl)) return;
          throw new Error(data?.error || "Failed to load Shopify blogs");
        }
        const list = (data?.blogs || []).map((blog: any) => ({
          id: blog.id,
          title: blog.title,
        }));
        setBlogs(list);
        if (!selectedBlogId && list.length > 0) {
          setSelectedBlogId(list[0].id);
        }
      } catch (err: any) {
        if (!active) return;
        setBlogsError(err?.message || "Failed to load Shopify blogs");
      } finally {
        if (active) setBlogsLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [
    attemptReauth,
    statusLoading,
    hasMapping,
    isConnected,
    normalizedShop,
    host,
    forceReconnectMode,
  ]);

  useEffect(() => {
    if (isConnected) {
      setForceReconnectMode(false);
      stopConnectionPolling();
    }
  }, [isConnected, stopConnectionPolling]);

  useEffect(() => {
    if (!host || !normalizedShop) return;

    const refreshStatus = () => {
      if (document.visibilityState === "hidden") return;
      window.setTimeout(() => {
        void fetchNativeConnectionStatus({ silent: true });
      }, 1500);
    };

    window.addEventListener("focus", refreshStatus);
    document.addEventListener("visibilitychange", refreshStatus);

    return () => {
      window.removeEventListener("focus", refreshStatus);
      document.removeEventListener("visibilitychange", refreshStatus);
    };
  }, [fetchNativeConnectionStatus, host, normalizedShop]);

  useEffect(() => {
    if (!normalizedShop) return;

    const handleGenseoMessage = (event: MessageEvent) => {
      if (!event.origin.startsWith(getGenseoBaseUrl().replace(/\/$/, ""))) return;
      const payload = event.data as { type?: string; shopDomain?: string; productSyncEnabled?: boolean };
      if (payload?.type !== "genseo:shopify:connected") return;
      if (payload.shopDomain && normalizeShopDomain(payload.shopDomain) !== normalizedShop) return;

      trackAnalyticsEvent("genseo_connection_completed", {
        route: "/",
        shopDomain: normalizedShop,
        productSyncEnabled: payload.productSyncEnabled,
      });
      setIsConnected(true);
      setForceReconnectMode(false);
      setUiStep("CONNECT");
      stopConnectionPolling();

      if (typeof payload.productSyncEnabled === "boolean") {
        setProductSyncEnabled(payload.productSyncEnabled);
        if (payload.productSyncEnabled) {
          pendingInitialProductSyncRef.current = true;
          try {
            window.localStorage.setItem(getPendingProductSyncStorageKey(normalizedShop), "true");
          } catch {}
        }
      }

      window.setTimeout(() => {
        void fetchNativeConnectionStatus({ silent: true });
      }, 1500);
    };

    window.addEventListener("message", handleGenseoMessage);
    return () => {
      window.removeEventListener("message", handleGenseoMessage);
    };
  }, [fetchNativeConnectionStatus, normalizedShop, stopConnectionPolling]);

  const startInitialProductSync = useCallback(async () => {
    if (!host || !normalizedShop) return;
    if (!pendingInitialProductSyncRef.current) return;
    if (initialProductSyncInFlightRef.current) return;
    if (initialProductSyncAttemptedForShopRef.current === normalizedShop) return;

    initialProductSyncInFlightRef.current = true;
    initialProductSyncAttemptedForShopRef.current = normalizedShop;
    setProductSyncStatus("syncing");
    setProductSyncError(null);

    try {
      trackAnalyticsEvent("product_sync_started", {
        route: "/",
        shopDomain: normalizedShop,
        source: "initial_connect",
      });
      const syncRes = await fetchWithSessionToken(host, "/api/sync-products", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shop: normalizedShop }),
      });
      const syncData = await syncRes.json().catch(() => ({}));

      if (!syncRes.ok) {
        throw new Error(syncData?.error || "Failed to sync Shopify products");
      }

      if (isMountedRef.current) {
        trackAnalyticsEvent("product_sync_completed", {
          route: "/",
          shopDomain: normalizedShop,
          source: "initial_connect",
        });
        setProductSyncStatus("synced");
        setLastProductSyncAt(new Date().toISOString());
        setProductSyncError(null);
      }
      pendingInitialProductSyncRef.current = false;
      window.localStorage.removeItem(getPendingProductSyncStorageKey(normalizedShop));
    } catch (err: any) {
      trackAnalyticsEvent("product_sync_failed", {
        route: "/",
        shopDomain: normalizedShop,
        source: "initial_connect",
      });
      if (isMountedRef.current) {
        setProductSyncStatus("failed");
        setProductSyncError(err?.message || "Failed to sync Shopify products");
      }
      pendingInitialProductSyncRef.current = false;
      window.localStorage.removeItem(getPendingProductSyncStorageKey(normalizedShop));
    } finally {
      initialProductSyncInFlightRef.current = false;
    }
  }, [host, normalizedShop]);

  const saveProductSyncPreference = useCallback(
    async (enabled: boolean, options: { blockOnFailure?: boolean } = {}) => {
      if (!host || !normalizedShop) return false;

      try {
        window.localStorage.setItem(
          getProductSyncPreferenceStorageKey(normalizedShop),
          enabled ? "true" : "false"
        );
      } catch {}

      try {
        trackAnalyticsEvent("product_sync_preference_changed", {
          route: "/",
          shopDomain: normalizedShop,
          productSyncEnabled: enabled,
        });
        const settingsRes = await fetchWithSessionToken(host, "/api/product-sync-settings", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            shop: normalizedShop,
            productSyncEnabled: enabled,
            genseoApiBaseUrl: `${getGenseoBaseUrl().replace(/\/$/, "")}/api/integrations/shopify`,
          }),
        });
        const settingsData = await settingsRes.json().catch(() => ({}));

        if (!settingsRes.ok) {
          throw new Error(settingsData?.error || "Failed to save product sync setting");
        }

        setProductSyncEnabled(Boolean(settingsData?.productSyncEnabled));
        setProductSyncStatus(settingsData?.productSyncStatus || "idle");
        setLastProductSyncAt(settingsData?.lastProductSyncAt || null);
        setProductSyncError(settingsData?.productSyncError || null);
        return true;
      } catch (err: any) {
        setProductSyncError(err?.message || "Failed to save product sync setting");
        if (options.blockOnFailure) {
          throw err;
        }
        return false;
      }
    },
    [host, normalizedShop]
  );

  useEffect(() => {
    let desiredProductSyncEnabled: boolean | null = null;
    if (normalizedShop) {
      try {
        const storedPreference = window.localStorage.getItem(
          getProductSyncPreferenceStorageKey(normalizedShop)
        );
        if (storedPreference === "true") desiredProductSyncEnabled = true;
        if (storedPreference === "false") desiredProductSyncEnabled = false;
      } catch {}
    }

    if (
      !isConnected ||
      forceReconnectMode ||
      productSyncStatus === "syncing"
    ) {
      return;
    }

    if (desiredProductSyncEnabled === false) {
      void saveProductSyncPreference(false).then((saved) => {
        if (!saved || !normalizedShop) return;
        pendingInitialProductSyncRef.current = false;
        initialProductSyncAttemptedForShopRef.current = null;
        window.localStorage.removeItem(getPendingProductSyncStorageKey(normalizedShop));
        window.localStorage.removeItem(getProductSyncPreferenceStorageKey(normalizedShop));
      });
      return;
    }

    const shouldSyncProducts =
      desiredProductSyncEnabled === true ||
      (productSyncEnabled && pendingInitialProductSyncRef.current);

    if (!shouldSyncProducts) return;

    void saveProductSyncPreference(true).then((saved) => {
      if (saved) void startInitialProductSync();
    });
  }, [
    forceReconnectMode,
    isConnected,
    productSyncEnabled,
    productSyncStatus,
    normalizedShop,
    saveProductSyncPreference,
    startInitialProductSync,
  ]);

  const openManagedPricing = useCallback(
    async (plan: PlanTier) => {
      if (!host || !normalizedShop) return;
      try {
        setStatusError(null);
        setSelectedPlan(plan);
        setIsOpeningPricing(true);
        trackAnalyticsEvent("plan_selected", {
          route: "/",
          shopDomain: normalizedShop,
          plan,
        });
        const planKey = plan === "STARTER" ? "STARTER" : "BUSINESS";
        trackAnalyticsEvent("shopify_billing_started", {
          route: "/",
          shopDomain: normalizedShop,
          plan: planKey,
        });
        const res = await fetchWithSessionToken(host, "/api/billing/start", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            shop: normalizedShop,
            host,
            plan: planKey,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (attemptReauth(data?.reauthorizeUrl)) return;
          throw new Error(data?.error || "Failed to start Shopify billing");
        }

        if (data?.active) {
          const gateStatus = await resolveEntitlementGate({ retryForReturn: true });
          if (gateStatus.reauthorized) {
            return;
          }
          setSubscriptionStatus(gateStatus.subscriptionStatus);
          setSubscriptionName(gateStatus.subscriptionName);
          setHasActiveSubscription(gateStatus.hasActiveSubscription);
          setUiStep("CONNECT");
          return;
        }

        const managedPricingUrl = data?.managedPricingUrl as string | undefined;
        if (!managedPricingUrl) {
          throw new Error(
            "Managed pricing URL missing. Please verify Shopify managed pricing setup."
          );
        }

        markManagedPricingReturn();

        // For Shopify admin managed pricing, direct top-level navigation avoids
        // malformed embedded redirects with duplicated /store/<handle> segments.
        if (window.top) {
          window.top.location.href = managedPricingUrl;
        } else {
          window.location.href = managedPricingUrl;
        }
      } catch (err: any) {
        setStatusError(err?.message || "Failed to open Shopify pricing");
      } finally {
        setIsOpeningPricing(false);
      }
    },
    [
      host,
      markManagedPricingReturn,
      normalizedShop,
      resolveEntitlementGate,
    ]
  );

  const handleDisconnect = async () => {
    if (!host || !normalizedShop) return;
    setDisconnecting(true);
    setStatusError(null);
    try {
      trackAnalyticsEvent("disconnect_started", {
        route: "/",
        shopDomain: normalizedShop,
      });
      const res = await fetchWithSessionToken(host, "/api/native-disconnect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shop: normalizedShop }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || "Failed to disconnect");
      }
      stopConnectionPolling();
      setIsConnected(false);
      setHasMapping(false);
      setForceReconnectMode(true);
      setDisconnectOpen(false);
      trackAnalyticsEvent("disconnect_completed", {
        route: "/",
        shopDomain: normalizedShop,
      });
    } catch (err: any) {
      setStatusError(err?.message || "Failed to disconnect");
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSkipShopifyPayment = () => {
    if (normalizedShop) {
      trackAnalyticsEvent("plan_selected", {
        route: "/",
        shopDomain: normalizedShop,
        plan: "EXISTING_GENSEO",
      });
      try {
        window.localStorage.setItem(getSkipShopifyPaymentStorageKey(normalizedShop), "true");
      } catch {}
    }
    setUiStep("CONNECT");
  };

  const handleFieldChange = (key: string, value: string) => {
    setFieldMap((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyProductSyncPreference = async () => {
    if (!host || !normalizedShop) return;

    pendingInitialProductSyncRef.current = productSyncEnabled;
    initialProductSyncAttemptedForShopRef.current = null;
    if (productSyncEnabled) {
      window.localStorage.setItem(getPendingProductSyncStorageKey(normalizedShop), "true");
      setProductSyncStatus("idle");
      setProductSyncError(null);
    } else {
      window.localStorage.removeItem(getPendingProductSyncStorageKey(normalizedShop));
    }

    try {
      window.localStorage.setItem(
        getProductSyncPreferenceStorageKey(normalizedShop),
        productSyncEnabled ? "true" : "false"
      );
    } catch {}

    if (isConnected) {
      await saveProductSyncPreference(productSyncEnabled, { blockOnFailure: false });
    }
  };

  const handleConnectMapping = async (
    options: { silent?: boolean; redirect?: boolean } = {}
  ) => {
    if (!host || !normalizedShop || !selectedBlogId) return;
    setMappingSaving(true);
    if (!options.silent) {
      setStatusError(null);
    }
    try {
      const params = new URLSearchParams({ shop: normalizedShop });
      if (host) params.set("host", host);

      const pendingRes = await fetchWithSessionToken(
        host,
        `/api/native-mapping/pending?${params.toString()}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            blogId: selectedBlogId,
            fieldMap,
          }),
        }
      );
      const pendingData = await pendingRes.json().catch(() => ({}));
      if (!pendingRes.ok) {
        throw new Error(pendingData?.error || "Failed to save mapping selection");
      }

      trackAnalyticsEvent("mapping_saved", {
        route: "/",
        shopDomain: normalizedShop,
        source: "connect_flow",
      });

      await applyProductSyncPreference();

      if (options.redirect && targetUrl) {
        trackAnalyticsEvent("genseo_connection_started", {
          route: "/",
          shopDomain: normalizedShop,
          productSyncEnabled,
        });
        if (hasActiveSubscription) {
          const gateStatus = await resolveEntitlementGate({ retryForReturn: true });
          if (gateStatus.reauthorized) {
            return;
          }
          setSubscriptionStatus(gateStatus.subscriptionStatus);
          setSubscriptionName(gateStatus.subscriptionName);
          setHasActiveSubscription(gateStatus.hasActiveSubscription);
        }

        const newWindow = window.open(targetUrl, "_blank");
        if (!newWindow) {
          const redirected = redirectToExternal(host, targetUrl);
          if (!redirected) {
            if (window.top) {
              window.top.location.href = targetUrl;
            } else {
              window.location.href = targetUrl;
            }
          }
        }
        await startConnectionPolling();
      }
    } catch (err: any) {
      if (!options.silent) {
        setStatusError(err?.message || "Failed to save Shopify mapping");
      }
    } finally {
      setMappingSaving(false);
    }
  };

  const openManagedPricingDashboard = useCallback(async () => {
    if (!host || !normalizedShop) return;
    try {
      setStatusError(null);
      const params = new URLSearchParams({ shop: normalizedShop, host });
      const res = await fetchWithSessionToken(host, `/api/billing/manage?${params.toString()}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (attemptReauth(data?.reauthorizeUrl)) return;
        throw new Error(data?.error || "Failed to open managed subscriptions");
      }
      const managedPricingUrl = data?.managedPricingUrl as string | undefined;
      if (!managedPricingUrl) {
        throw new Error("Managed pricing URL missing.");
      }
      trackAnalyticsEvent("external_dashboard_docs_links_opened", {
        route: "/",
        shopDomain: normalizedShop,
        destination: "shopify_managed_subscriptions",
      });
      if (window.top) {
        window.top.location.href = managedPricingUrl;
      } else {
        window.location.href = managedPricingUrl;
      }
    } catch (err: any) {
      setStatusError(err?.message || "Failed to open managed subscriptions");
    }
  }, [attemptReauth, host, normalizedShop]);

  const pricingPlans: Array<{
    id: PlanTier;
    name: string;
    price: string;
    description: string;
    features: string[];
    badge?: string;
  }> = [
    {
      id: "EXISTING_GENSEO",
      name: "Free to install",
      price: "$0",
      description:
        "Use this if you already pay for Genseo directly.",
      features: [
        "No Shopify subscription charge",
        "Connect Shopify",
        "Setup Genseo",
      ],
    },
    {
      id: "STARTER",
      name: "Starter",
      price: "$59",
      description:
        "Perfect for one website with up to 10 monthly articles. Ideal for one-man-shows.",
      features: [
        "10 SEO/GEO Optimized Articles",
        "Personalized Keyword Strategy",
        "5 Keyword Research Credits",
        "Auto Business Promotion Within Articles",
        "Auto Internal Linking",
        "Auto-Publishing to Your Website",
        "Auto AI Images",
        "Supports 75+ languages",
        "Chat Support",
      ],
    },
    {
      id: "PRO",
      name: "Business",
      price: "$99",
      description:
        "Perfect for one website with up to 30 monthly articles. Ideal for creators and businesses.",
      features: [
        "30 SEO/GEO Optimized Articles",
        "Personalized Keyword Strategy",
        "30 Keyword Research Credits",
        "Auto Business Promotion Within Articles",
        "Auto Internal Linking",
        "Auto-Publishing to Your Website",
        "Auto AI Images",
        "Supports 75+ languages",
        "Priority Chat Support",
      ],
      badge: "Most recommended",
    },
  ];
  const subscriptionSummary = subscriptionStatus
    ? `${subscriptionName ? `${subscriptionName} • ` : ""}Status: ${normalizeSubscriptionStatusLabel(subscriptionStatus)}`
    : null;
  const showConnectedScreen = !isPaymentTestMode && !statusLoading && isConnected && !forceReconnectMode;

  return (
    <div style={{ minHeight: "100vh", background: "var(--p-color-bg)" }}>
      <Page>
        <div
          style={{
            maxWidth: uiStep === "SUBSCRIPTION" ? 980 : 520,
            margin: showConnectedScreen ? "0 auto" : "1.5rem auto 0",
            minHeight: showConnectedScreen ? "calc(100vh - 12rem)" : undefined,
            display: showConnectedScreen ? "flex" : undefined,
            alignItems: showConnectedScreen ? "center" : undefined,
            justifyContent: showConnectedScreen ? "center" : undefined,
          }}
        >
          <Box paddingBlockStart="600" paddingBlockEnd="600">
            <BlockStack gap="300">
                {statusLoading ? (
                  <BlockStack gap="300">
                    <InlineStack align="center">
                      <div
                        className="genseo-status-skeleton__logo"
                        style={{
                          borderRadius: 10,
                          background: "#e5e7eb",
                          width: 44,
                          height: 44,
                        }}
                      />
                    </InlineStack>
                    <BlockStack gap="200" align="center">
                      <div style={{ width: "220px" }}>
                        <SkeletonDisplayText size="small" />
                      </div>
                      <div style={{ width: "360px", maxWidth: "100%" }}>
                        <SkeletonBodyText lines={2} />
                      </div>
                      <div style={{ width: "170px", maxWidth: "65%" }}>
                        <div className="genseo-status-skeleton__mini-block" />
                      </div>
                    </BlockStack>
                  </BlockStack>
                ) : (
                  <>
                    <InlineStack align="center">
                      <div
                        style={{
                          borderRadius: 10,
                          background: "#f1f1f1",
                          width: 40,
                          height: 40,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Image
                          source="https://cdn.prod.website-files.com/68c849d1ac1eefc5d6925866/68e647f7aac59c04a82f7121_7d0c3f8f2cb8c1bd4dc11bab861c79cc_Logo%20-%20256.jpg"
                          alt="Genseo"
                          width={40}
                          style={{
                            display: "block",
                            width: 40,
                            height: 40,
                            objectFit: "cover",
                            borderRadius: 10,
                          }}
                        />
                      </div>
                    </InlineStack>
                    <Box paddingBlockEnd={uiStep === "SUBSCRIPTION" ? "400" : "200"}>
                      <BlockStack gap="200" align="center">
                        <Text as="h1" variant="headingLg" alignment="center">
                          {showConnectedScreen
                            ? "You're connected"
                            : uiStep === "SUBSCRIPTION"
                              ? "Select your Genseo Plan"
                              : "Connect your Shopify store"}
                        </Text>
                        <Text as="p" tone="subdued" alignment="center">
                          {showConnectedScreen
                            ? "Your Shopify store is linked with Genseo."
                            : uiStep === "SUBSCRIPTION"
                              ? "Choose how you want to activate Genseo for this Shopify store."
                              : "Choose your blog and field mapping, then continue setup in Genseo."}
                        </Text>
                        {subscriptionSummary && (
                          <Text as="p" tone="subdued" variant="bodySm" alignment="center">
                            {subscriptionSummary}
                          </Text>
                        )}
                      </BlockStack>
                    </Box>
                  </>
                )}
                {(error || statusError) && (
                  <Banner tone="critical">
                    <p>{error || statusError}</p>
                  </Banner>
                )}
                {(forceReconnectMode || !statusLoading) && (isPaymentTestMode || !isConnected || forceReconnectMode) && (
                  <BlockStack gap="300">
                    {uiStep === "SUBSCRIPTION" && (
                      <BlockStack gap="0">
                        <div className="existing-genseo-plan-card">
                          <Card padding="0">
                            <div className="existing-genseo-plan-card__inner">
                              <InlineStack align="space-between" blockAlign="center" gap="300">
                                <BlockStack gap="100">
                                  <Text as="p" variant="bodyMd" fontWeight="semibold">
                                    Already have a Genseo plan?
                                  </Text>
                                  <Text as="p" tone="subdued" variant="bodySm">
                                    Use your existing plan and continue with the Shopify setup.
                                  </Text>
                                </BlockStack>
                                <div className="skip-shopify-payment-action">
                                  <Button variant="primary" onClick={handleSkipShopifyPayment}>
                                    Skip Shopify payment
                                  </Button>
                                  <Text as="span" tone="subdued" variant="bodySm">
                                    Continue setup
                                  </Text>
                                </div>
                              </InlineStack>
                            </div>
                          </Card>
                        </div>
                        <div className="subscription-plan-grid">
                          {pricingPlans.map((plan) => {
                            const isSelected = selectedPlan === plan.id;
                            const isSlightlyNarrow = plan.id === "EXISTING_GENSEO" || plan.id === "STARTER";
                            return (
                              <div
                                key={plan.id}
                                className={isSlightlyNarrow ? "subscription-plan-card--narrow" : undefined}
                              >
                                <PricingCard
                                  title={plan.name}
                                  featuredText={plan.badge}
                                  description={plan.description}
                                  features={plan.features}
                                  price={plan.price}
                                  frequency="month"
                                  button={{
                                    content:
                                      hasActiveSubscription
                                        ? "Continue"
                                        : plan.id === "EXISTING_GENSEO"
                                          ? "Choose Free"
                                          : plan.id === "STARTER"
                                          ? "Choose Starter"
                                          : "Choose Business",
                                    props: {
                                      variant: plan.id === "PRO" ? "primary" : "secondary",
                                      onClick: hasActiveSubscription
                                        ? () => setUiStep("CONNECT")
                                        : plan.id === "EXISTING_GENSEO"
                                          ? handleSkipShopifyPayment
                                          : () => openManagedPricing(plan.id),
                                      loading: hasActiveSubscription
                                        ? false
                                        : plan.id !== "EXISTING_GENSEO" && isOpeningPricing && isSelected,
                                      disabled: isOpeningPricing,
                                    },
                                  }}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </BlockStack>
                    )}

                    {uiStep === "CONNECT" && (
                      <BlockStack gap="300">
                        <Card>
                          <Box paddingInline="300">
                            <div className="product-sync-toggle-row">
                              <div className="product-sync-toggle-row__content">
                                <BlockStack gap="100">
                                  <InlineStack gap="200" blockAlign="center">
                                    <Text as="p" variant="bodyMd" fontWeight="semibold">
                                      <Tooltip content="You can disable this at any time or remove products from sync in Genseo under Profile > Products.">
                                        <span className="product-sync-tooltip-trigger">
                                          Sync Shopify products
                                        </span>
                                      </Tooltip>
                                    </Text>
                                    <Badge tone={productSyncEnabled ? "success" : "critical"}>
                                      {productSyncEnabled ? "Enabled" : "Disabled"}
                                    </Badge>
                                  </InlineStack>
                                  <Text as="p" tone="subdued" variant="bodySm">
                                    Import products now and keep future Shopify product changes synced with Genseo.
                                  </Text>
                                </BlockStack>
                              </div>
                              <div className="product-sync-toggle-row__control">
                                <Knob
                                  selected={productSyncEnabled}
                                  ariaLabel="Toggle Shopify product sync"
                                  onClick={() => {
                                    const nextProductSyncEnabled = !productSyncEnabled;
                                    trackAnalyticsEvent("product_sync_preference_changed", {
                                      route: "/",
                                      shopDomain: normalizedShop,
                                      productSyncEnabled: nextProductSyncEnabled,
                                      source: "connect_flow_toggle",
                                    });
                                    setProductSyncEnabled(nextProductSyncEnabled);
                                  }}
                                />
                              </div>
                            </div>
                          </Box>
                        </Card>
                        <Card>
                          <Box paddingInline="300" paddingBlockStart="200" paddingBlockEnd="200">
                            <BlockStack gap="300">
                              <BlockStack gap="100">
                                <Text as="p" variant="bodyMd" fontWeight="semibold">
                                  Configure Shopify blog sync
                                </Text>
                                <Text as="p" tone="subdued" variant="bodySm">
                                  Choose where Genseo should publish articles and how fields map to Shopify.
                                </Text>
                              </BlockStack>
                              <Select
                                label="Select Shopify blog"
                                options={
                                  blogs.length > 0
                                    ? blogs.map((blog) => ({
                                        label: blog.title,
                                        value: blog.id,
                                      }))
                                    : [{ label: "No blogs found", value: "" }]
                                }
                                value={selectedBlogId}
                                onChange={setSelectedBlogId}
                                disabled={blogsLoading || blogs.length === 0}
                              />
                              <BlockStack gap="200">
                                {Object.keys(fieldMap).map((key) => (
                                  <InlineStack key={key} gap="200" align="space-between">
                                    <Text as="span">{fieldLabels[key] || key}</Text>
                                    <div style={{ minWidth: 180 }}>
                                      <Select
                                        label=""
                                        labelHidden
                                        options={shopifyFieldOptions}
                                        value={fieldMap[key]}
                                        onChange={(value) => handleFieldChange(key, value)}
                                      />
                                    </div>
                                  </InlineStack>
                                ))}
                              </BlockStack>
                              <Button
                                variant="primary"
                                fullWidth
                                onClick={() => handleConnectMapping({ redirect: true })}
                                loading={mappingSaving || isConnectionPolling}
                                disabled={
                                  blogsLoading ||
                                  !selectedBlogId ||
                                  isConnectionPolling
                                }
                              >
                                Connect
                              </Button>
                              <InlineStack align="center">
                                <Button
                                  variant="plain"
                                  size="slim"
                                  onClick={openManagedPricingDashboard}
                                >
                                  Manage subscriptions
                                </Button>
                              </InlineStack>
                              {blogsError && (
                                <Banner tone="critical">
                                  <p>{blogsError}</p>
                                </Banner>
                              )}
                            </BlockStack>
                          </Box>
                        </Card>
                      </BlockStack>
                    )}
                  </BlockStack>
                )}
                {showConnectedScreen && (
                  <BlockStack gap="300">
                    {productSyncStatus === "syncing" && (
                      <Banner tone="info">
                        <p>Shopify product sync is running. You can continue while products are imported.</p>
                      </Banner>
                    )}
                    {productSyncStatus === "failed" && (
                      <Banner tone="warning">
                        <p>Shopify is connected, but the last product sync failed. You can retry from Genseo.</p>
                      </Banner>
                    )}
                    <InlineStack align="center" gap="300">
                      <Button
                        variant="primary"
                        url={connectedUrl || targetUrl}
                        external
                        onClick={() =>
                          trackAnalyticsEvent("external_dashboard_docs_links_opened", {
                            route: "/",
                            shopDomain: normalizedShop,
                            destination: "genseo_dashboard",
                          })
                        }
                      >
                        Open Genseo
                      </Button>
                      <Button
                        variant="tertiary"
                        tone="critical"
                        onClick={() => setDisconnectOpen(true)}
                      >
                        Disconnect
                      </Button>
                    </InlineStack>
                  </BlockStack>
                )}
            </BlockStack>
          </Box>
        </div>
        <Modal
          open={disconnectOpen}
          onClose={() => setDisconnectOpen(false)}
          title="Disconnect Shopify"
          primaryAction={{
            content: disconnecting ? "Disconnecting..." : "Disconnect",
            destructive: true,
            onAction: handleDisconnect,
            loading: disconnecting,
          }}
          secondaryActions={[
            {
              content: "Cancel",
              onAction: () => setDisconnectOpen(false),
            },
          ]}
        >
          <Modal.Section>
            <Text as="p">
              This will disconnect your Shopify store from Genseo. You can reconnect at any time.
            </Text>
          </Modal.Section>
        </Modal>
      </Page>
    </div>
  );
}

export default ConnectFlowPage;
